/**
 * Titan Electric — client-side API helper
 *
 * Runs in the BROWSER. Talks to the deployed Express/SQLite server over
 * HTTP — never requires server-only modules like better-sqlite3 (./db).
 *
 * This file exists for components not yet migrated to the newer
 * services/auth.js + services/employees.js pattern. As each component
 * gets migrated, its calls here can be removed.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try {
    const raw = localStorage.getItem('titan_session');
    return raw ? JSON.parse(raw).token : null;
  } catch {
    return null;
  }
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['x-worker-token'] = token;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// Used by AuthContext.js, AdminDashboard.js — true when no live API/Trello
// connection is configured (kept for compatibility with existing callers;
// the SQLite-backed server is always "live", so this returns false).
export function isMockMode() {
  return false;
}

// Used by AuthContext.js, AdminDashboard.js — returns the user's Trello
// boards via the server's Trello-sync layer. The server itself talks to
// Trello directly; this just calls through to it.
export async function getAllBoards() {
  const data = await apiFetch('/sync/trello', { method: 'POST', body: JSON.stringify({}) });
  return data.boards || [];
}

// Used by AdminDashboard.js
export async function getBoardMembers(boardId) {
  const data = await apiFetch('/sync/trello', { method: 'POST', body: JSON.stringify({}) });
  return data.members || [];
}

// Used by Dashboard.js, AskAI.js — a worker's assigned tasks, pulled from
// the database (kept in sync with Trello via webhooks server-side).
export async function getMemberCards(trelloId) {
  return apiFetch('/my-tasks');
}

// Used by JobDetail.js
export async function getCardDetails(taskId) {
  return apiFetch(`/tasks/${taskId}`);
}

export async function postComment(taskId, text) {
  return apiFetch(`/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// Attachments aren't wired to the database yet — this is a placeholder
// that avoids a hard crash in JobDetail.js until that migration happens.
export async function uploadAttachment(taskId, file) {
  console.warn('uploadAttachment: not yet implemented against the database backend');
  return { success: false, error: 'Attachments are not yet available.' };
}
